/**
 * SystemSidebar - Sidebar de navegação para System Admin (MASTER + ORG_OWNER)
 * 
 * MASTER sees: Overview, Organizations, Master Users
 * ORG_OWNER sees: Overview, Tenants
 */

import { memo } from 'react';
import { LayoutDashboard, Shield, Landmark, Building2, Flag, Activity, Package } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { VersionBadge } from '@/components/layout/VersionBadge';
import { AppLogo } from '@/components/ui/AppLogo';
import type { UserRole } from '@/types/auth.types';

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  /** Roles that can see this nav item */
  roles: UserRole[];
}

/**
 * Build nav items dynamically.
 * For ORG_OWNER, "My Organization" links to /system/organizations/:orgId (not /system).
 */
function buildNavItems(organizationId?: string): NavItem[] {
  const orgOverviewPath = organizationId
    ? `/system/organizations/${organizationId}`
    : '/system/tenants';

  return [
    { icon: Activity, label: 'Dashboard', path: '/system/dashboard', roles: ['master', 'org_owner'] },
    { icon: LayoutDashboard, label: 'Overview', path: '/system', roles: ['master'] },
    { icon: LayoutDashboard, label: 'My Organization', path: orgOverviewPath, roles: ['org_owner'] },
    { icon: Building2, label: 'Tenants', path: '/system/tenants', roles: ['org_owner'] },
    { icon: Package, label: 'Plans', path: '/system/plans', roles: ['org_owner'] },
    { icon: Landmark, label: 'Organizations', path: '/system/organizations', roles: ['master'] },
    { icon: Shield, label: 'Master Users', path: '/system/masters', roles: ['master'] },
    { icon: Flag, label: 'Feature Flags', path: '/system/feature-flags', roles: ['master'] },
  ];
}

export const SystemSidebar = memo(function SystemSidebar() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const userRole = user?.role || 'master';

  const navItems = buildNavItems(user?.organizationId);
  const filteredItems = navItems.filter((item) => item.roles.includes(userRole));

  const isActive = (path: string) => {
    if (path === '/system') {
      return location.pathname === '/system';
    }
    return location.pathname.startsWith(path);
  };

  const isOrgOwner = userRole === 'org_owner';

  return (
    <div className="flex h-screen w-[260px] flex-col border-r border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
      {/* Logo Header */}
      <div className="flex items-center gap-3 border-b border-zinc-200 px-6 py-6 dark:border-zinc-800">
        <div className="flex flex-col gap-1">
          <AppLogo
            iconClassName="h-9 w-9 bg-brand-primary"
            showName
            nameClassName="text-xl text-zinc-900 dark:text-zinc-50"
          />
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {isOrgOwner ? 'Tenant Management' : 'Master Control'}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col gap-1 px-3 py-4">
        {filteredItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center gap-3 rounded-lg px-4 py-3 transition-colors',
                active
                  ? 'bg-primary'
                  : 'hover:bg-zinc-100 dark:hover:bg-zinc-900',
              )}
            >
              <Icon
                className={cn(
                  'h-5 w-5 shrink-0',
                  active
                    ? 'text-primary-foreground'
                    : 'text-zinc-600 dark:text-zinc-400',
                )}
              />
              <span
                className={cn(
                  'text-sm',
                  active
                    ? 'font-semibold text-primary-foreground'
                    : 'font-medium text-zinc-600 dark:text-zinc-400',
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Footer Info */}
      <div className="border-t border-zinc-200 px-4 py-4 dark:border-zinc-800">
        <p className="text-xs text-zinc-500 dark:text-zinc-600">
          {isOrgOwner ? 'Organization Owner Access' : 'Master Access Level'}
        </p>
        <div className="mt-1">
          <VersionBadge />
        </div>
      </div>
    </div>
  );
});
